import * as React from "react";
import { observable, computed } from "mobx"
import { observer, Provider } from "mobx-react";
import { Area } from "../General/Area";
import { AreaBody } from "../General/AreaBody"
import * as Utilities from "../Utilities";
import { IRouteProps } from "../RouterUtility";
import { Strings } from "../Stores/StringsStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { AppStore } from "../Stores/AppStore";
import { Urls } from "../Urls";
import * as SettingsManagement from "../SettingsManagement";
import { InputChoice, ValueCaptionPair, InputBoolean, InputFile } from "../Shared";
import { InputChoiseComponent } from "../Inputs/InputChoiseComponent";
import { InputBooleanComponent } from "../Inputs/InputBooleanComponent";
import * as UiStore from "../States/AreaUiInfo";
import { ContentGroup } from "../General/ContentGroup";
import { ButtonGroup } from "../Buttons/ButtonGroup";
import { CloseButton } from "../Buttons/CloseButton";
import { Toolbar } from "../General/Toolbar";
import { ExtendedAreaUiInfoImpl } from "../States/AreaUiInfo";
import { UiRepos } from "../Stores/UiStore";
import { MetaDataTranslationExportButton } from "../Buttons/MetaDataTranslationExportButton";
import { MetaDataTranslationImportButton } from "../Buttons/MetaDataTranslationImportButton";
import { DialogStore } from "../Stores/DialogStore";
import {  LanguageInfo } from "../Shared";

class TranslationModel {
	@observable srcLanguage: InputChoice = new InputChoice();
	@observable tgtLanguage: InputChoice = new InputChoice();

	@observable ignoreApproved: InputBoolean = new InputBoolean();
	@observable ignoreState: InputBoolean = new InputBoolean();
	@observable ignoreSourceTimeStamp: InputBoolean = new InputBoolean();
	@observable ignoreTargetTimeStamp: InputBoolean = new InputBoolean();
	@observable file: File;
}

@observer
export class Translation extends React.Component<{}, {}> {

	@observable model: TranslationModel = new TranslationModel();
	areaUiInfo: ExtendedAreaUiInfoImpl = new ExtendedAreaUiInfoImpl(UiRepos.TRANSLATION_UI_INFO);

	hiddenStyle = { display: 'none' };
	style1 = { 'marginTop': '8px' };
	style2 = { 'marginLeft': '8px' };


	componentDidMount() {
		AppStore.setTitle(Strings.metaManagement.translation.title);

		this.getModel();
		this.areaUiInfo.contentGroupAccordionStatus.replace(UiRepos.translationUiInfo.contentGroupAccordionStatus);
		this.areaUiInfo.inputBooleanComponentStatus.replace(UiRepos.translationUiInfo.inputBooleanComponentStatus);
		this.areaUiInfo.inputChoiceComponentStatus.replace(UiRepos.translationUiInfo.inputChoiceComponentStatus);
	}
	get languageOptions() {
		let result: LanguageInfo[] = [];
		AppStore.context.contentLanguages
			.sort((a, b) => {
				if (a.caption < b.caption) return -1;
				if (a.caption > b.caption) return 1;
				return 0;
			})
			.forEach(lang => result.push(lang));
		return result;
	}

	getModel() {
		AppStore.isBusyModelLoading = true;
		SettingsManagement.getLanguagesAsync().then(model => {
			
			let languages: string[] = [];

			for (let l of this.languageOptions) {
					languages.push(l.name);

					let vcp: ValueCaptionPair = new ValueCaptionPair();
					vcp.caption = l.caption;
					vcp.value = l.name;

					this.model.srcLanguage.values.push(vcp);
					this.model.tgtLanguage.values.push(vcp);
				}

			if (languages.length > 1) {
				let uiLang: string = AppStore.context.sessionDetails.uiLanguage;
				let uiLangIndex: number = languages.indexOf(uiLang);
				if (uiLangIndex != -1) {
					this.model.srcLanguage.value = uiLang;

					for (let i = 0; i < languages.length; i++) {
						if (i != uiLangIndex) {
							this.model.tgtLanguage.value = languages[i];
							break;
						}
					}
				}
				else {
					this.model.srcLanguage.value = languages[0];
					this.model.tgtLanguage.value = languages[1];
				}

				this.model.srcLanguage.isValid = true;
				this.model.tgtLanguage.isValid = true;
			}


			this.model.srcLanguage.label = Strings.metaManagement.translation.translationSourceLanguageLabel;
			this.model.tgtLanguage.label = Strings.metaManagement.translation.translationTargetLanguageLabel;
			this.model.ignoreApproved.label = Strings.metaManagement.translation.xliffImportFlags.ignoreApproved;
			this.model.ignoreApproved.value = false;
			this.model.ignoreState.label = Strings.metaManagement.translation.xliffImportFlags.ignoreState;
			this.model.ignoreState.value = false;
			this.model.ignoreSourceTimeStamp.label = Strings.metaManagement.translation.xliffImportFlags.ignoreSourceTimeStamp;
			this.model.ignoreSourceTimeStamp.value = false;
			this.model.ignoreTargetTimeStamp.label = Strings.metaManagement.translation.xliffImportFlags.ignoreTargetTimeStamp;
			this.model.ignoreTargetTimeStamp.value = false;

			NotificationStore.clearMessage();
			AppStore.isBusyModelLoading = false;

		}).catch(error => {
			DialogStore.showError(Strings.general.errorDialogTitle, error);
		});
	}

	handleLanguageChange(inputChoice: InputChoice, invalidMessage: string): void {
		let src = this.model.srcLanguage.value;
		let tgt = this.model.tgtLanguage.value;

		if (src && tgt && src != tgt) {
			this.model.srcLanguage.isValid = true;
			this.model.tgtLanguage.isValid = true;
			this.model.srcLanguage.validationMessage = null;
			this.model.tgtLanguage.validationMessage = null;
		}
		else {
			inputChoice.isValid = false;
			inputChoice.validationMessage = Strings.metaManagement.translation.translationSourceLanguageInvalid;
		}
	}

	handleSrcLanguageChange = () => {
		this.handleLanguageChange(this.model.srcLanguage, Strings.metaManagement.translation.translationSourceLanguageInvalid);
	}

	handleTgtLanguageChange = () => {
		this.handleLanguageChange(this.model.tgtLanguage, Strings.metaManagement.translation.translationTargetLanguageInvalid);
	}

	downloadEnabled = (): boolean => {
		return this.model.srcLanguage.values.length > 1 && this.model.srcLanguage.isValid && this.model.tgtLanguage.isValid;
	}

	getDownloadUrl = (): string => {
		let url: string;
		let src: string = this.model.srcLanguage.value;
		let tgt: string = this.model.tgtLanguage.value;

		if (src && tgt) {
			url = "/api/xliff/export?sourceLanguage=" + src + "&targetLanguage=" + tgt;
		}

		return url;
	}

	downloadFile(url: string, fileName: string) {
		var dl = document.createElement("a");
		dl.setAttribute("href", url);
		dl.setAttribute("download", fileName);
		dl.setAttribute("display", "none");
		document.body.appendChild(dl);
		dl.click();
		document.body.removeChild(dl);
	}

	download = () => {
		this.downloadFile(this.getDownloadUrl(), "export.xml");
	}

	upload = () => {
		document.getElementById("uploadFileInput").click();
	}

	handleFileChanged = () => {
		let fileInput: HTMLInputElement = document.getElementById("uploadFileInput") as HTMLInputElement;

		if (fileInput.files.length == 1) {
			this.model.file = fileInput.files[0];
		}
		else {
			this.model.file = null;
		}
	}

	submitEnabled = (): boolean => {
		return this.model.file != null && this.model.file != undefined;
	}

	submit = () => {
		AppStore.isBusy = true;

		let flags: number = 0;
		if (this.model.ignoreApproved.value) {
			flags |= 1;
		}
		if (this.model.ignoreState.value) {
			flags |= 2;
		}
		if (this.model.ignoreSourceTimeStamp.value) {
			flags |= 4;
		}
		if (this.model.ignoreTargetTimeStamp.value) {
			flags |= 8;
		}

		let xmlHttp: XMLHttpRequest = new XMLHttpRequest();
		xmlHttp.open("PUT", "/api/xliff/import?settings=" + flags, true);
		xmlHttp.onreadystatechange = (ev: ProgressEvent) => {
			if (xmlHttp.readyState == 4) {
				if (xmlHttp.status == 200) {
					NotificationStore.showSuccessMessage(Strings.metaManagement.translation.translationImportSuccess);
					var dataUrl: string = "data:application/xml;charset=utf-8," + encodeURIComponent(xmlHttp.response);
					this.downloadFile(dataUrl, "import_result.xml");
				}
				else {
					DialogStore.showError(Strings.general.errorDialogTitle, Strings.metaManagement.translation.translationImportError);
				}

				AppStore.isBusy = false;
			}
		};

		xmlHttp.setRequestHeader('Content-Type', 'text/xml; charset=utf-8');

		var reader: FileReader = new FileReader();
		reader.onload = function () {
			xmlHttp.send(reader.result);
		};

		reader.onerror = function (ev) {
			DialogStore.showError(Strings.general.errorDialogTitle, Strings.metaManagement.translation.translationImportFileError);
			AppStore.isBusy = false;
		}

		let fileInput: HTMLInputElement = document.getElementById("uploadFileInput") as HTMLInputElement;
		reader.readAsText(fileInput.files[0]);
	}

	handleCancel = () => {
		AppStore.keepOnPage(Urls.home);
	}

	render() {
		return <Area areaUiInfo={this.areaUiInfo}>
			<AreaBody loading={AppStore.isBusyModelLoading}>
				<Toolbar>
					<span className="buttongroup is--buttongroup-right">
						<CloseButton onClick={this.handleCancel} testId="closeButton" />
					</span>
				</Toolbar>
				<div className="area-grid__body-content">
					<div className="content-main">

						<h1>{Strings.metaManagement.translation.title}</h1>

						<ContentGroup caption={Strings.metaManagement.translation.translationExportTitle} uiContext={this.areaUiInfo}>
							<ButtonGroup>
								<MetaDataTranslationExportButton disabled={!this.downloadEnabled()} onClick={this.download} testId="downloadButton" />
								<span className="tinytool__splitter"></span>
							</ButtonGroup>
							<InputChoiseComponent model={this.model.srcLanguage} onChange={this.handleSrcLanguageChange} testId="sourceLanguageSelect" uiContext={this.areaUiInfo} />
							<InputChoiseComponent model={this.model.tgtLanguage} onChange={this.handleTgtLanguageChange} testId="targetLanguageSelect" uiContext={this.areaUiInfo} />
						</ContentGroup>

						<ContentGroup caption={Strings.metaManagement.translation.translationImportTitle} uiContext={this.areaUiInfo}>
							<ButtonGroup>
								<MetaDataTranslationImportButton disabled={!this.submitEnabled()} onClick={this.submit} testId="submitButton" />
								<span className="tinytool__splitter"></span>
							</ButtonGroup>
							<div>
								<InputBooleanComponent model={this.model.ignoreApproved} testId="ignoreApprovedRadioCheck" uiContext={this.areaUiInfo} />
							</div>
							<div>
								<InputBooleanComponent model={this.model.ignoreState} testId="ignoreStateRadioCheck" uiContext={this.areaUiInfo} />
							</div>
							<div>
								<InputBooleanComponent model={this.model.ignoreSourceTimeStamp} testId="ignoreSourceTimeStampRadioCheck" uiContext={this.areaUiInfo} />
							</div>
							<div>
								<InputBooleanComponent model={this.model.ignoreTargetTimeStamp} testId="ignoreTargetTimeStampRadioCheck" uiContext={this.areaUiInfo} />
							</div>

							<div className="inside__note is--clickable-note" onClick={this.upload} data-test-id="uploadFileButton">
								<input style={this.hiddenStyle} type="file" onChange={this.handleFileChanged} accept="text/xml" id="uploadFileInput" data-test-id="uploadFileInput" />
								<span>{this.model.file ? this.model.file.name : Strings.metaManagement.translation.translationUploadFileLabel}</span>
							</div>
						</ContentGroup>
					</div>
				</div>
			</AreaBody>
		</Area>;
	}
}
